// src/components/exam-builder/SpeakingIntegratedRenderer.jsx
import React, { useEffect, useRef, useState } from "react";
import { BASE_URL } from "../../../config";

const pickMime = () => {
  const c = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  for (const m of c)
    if (
      typeof MediaRecorder !== "undefined" &&
      MediaRecorder.isTypeSupported?.(m)
    )
      return m;
  return "";
};
const fmt = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(
    s % 60
  ).padStart(2, "0")}`;
};

export default function SpeakingIntegratedRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  hideStem = false,
  onContinue,
  debug = false, // ⬅️ روشن کن تا لاگ‌ها رو ببینی
}) {
  const log = (...a) => {
    if (debug) console.log("[SIR]", ...a);
  };

  // ------- config -------
  const prompt = item?.body?.prompt || {};
  const audioCfg = prompt?.audio || {};
  const seg = audioCfg?.segment || {};
  const url = audioCfg?.url || "";

  const prepTimeSec = Math.max(
    0,
    Number(item?.body?.timings?.prepTimeSec ?? 15)
  );
  const respTimeSec = Math.max(
    1,
    Number(item?.body?.timings?.responseTimeSec ?? 60)
  );
  const minSec = Math.max(0, Number(item?.body?.capture?.minSec ?? 0));
  const maxSec = Math.max(
    1,
    Number(item?.body?.capture?.maxSec ?? respTimeSec)
  );
  const uploadOnStop = item?.body?.capture?.uploadOnStop !== false;

  const startSec = Number.isFinite(seg?.startSec)
    ? Math.max(0, seg.startSec)
    : 0;
  const endSec =
    Number.isFinite(seg?.endSec) && seg.endSec > startSec ? seg.endSec : null;

  // ------- state -------
  const [phase, setPhase] = useState(
    initialAnswers?.phase === "done" ? "done" : "audio"
  ); // audio -> prep -> record -> done
  const [autoplayNeeded, setAutoplayNeeded] = useState(false);
  const [prepLeft, setPrepLeft] = useState(prepTimeSec);
  const [respLeft, setRespLeft] = useState(respTimeSec);
  const [listenedMs, setListenedMs] = useState(initialAnswers?.listenedMs || 0);
  const [error, setError] = useState("");

  // refs
  const audioRef = useRef(null);
  const maxHeardRef = useRef(0);

  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recStartRef = useRef(0);
  const rafPrepRef = useRef(0);
  const rafRecRef = useRef(0);
  const didAutoNextRef = useRef(false);
  const recordingActiveRef = useRef(false);

  // برای ری‌استارت افکت صوت در تعویض آیتم
  const [audioBoot, setAudioBoot] = useState(0);

  // ====== Hard reset on item/url change ======
  useEffect(() => {
    log("reset:item", { id: item?._id, url });
    // cleanup record
    try {
      cancelAnimationFrame(rafRecRef.current);
    } catch {}
    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    try {
      mediaStreamRef.current?.getTracks()?.forEach((t) => t.stop());
    } catch {}
    mediaStreamRef.current = null;
    recordingActiveRef.current = false;

    // reset states
    setAutoplayNeeded(false);
    setPrepLeft(Math.max(0, Number(item?.body?.timings?.prepTimeSec ?? 15)));
    setRespLeft(
      Math.max(1, Number(item?.body?.timings?.responseTimeSec ?? 60))
    );
    setListenedMs(0);
    setError("");
    didAutoNextRef.current = false;
    maxHeardRef.current = 0;
    setPhase(initialAnswers?.phase === "done" ? "done" : "audio");
    setAudioBoot((v) => v + 1);
  }, [item?._id, url]); // ← خیلی مهم

  // ====== Phase 1: prompt audio (hidden, non-skippable) ======
  useEffect(() => {
    if (phase !== "audio") return;
    const el = audioRef.current;
    if (!el) return;

    log("audio:start", { startSec, endSec, src: url });

    const tryPlay = () =>
      el.play().catch(() => {
        log("audio:autoplay-blocked");
        setAutoplayNeeded(true);
      });

    const onLoadedMetadata = () => {
      if (startSec) el.currentTime = startSec;
      log("audio:loadedmetadata", {
        duration: el.duration,
        currentTime: el.currentTime,
      });
      tryPlay();
    };

    const onCanPlay = () => {
      // بعضی براوزرها قبل از canplay پخش نمی‌کنند
      tryPlay();
    };

    const finalizeAudio = (reason) => {
      try {
        el.pause();
      } catch {}
      const endPoint = endSec ?? el.duration ?? maxHeardRef.current;
      const listened =
        Math.max(0, Math.min(endPoint, maxHeardRef.current) - startSec) * 1000;
      setListenedMs(Math.floor(listened));
      log("audio:end→prep", { reason, listenedMs: Math.floor(listened) });
      setPhase("prep");
    };

    const onTimeUpdate = () => {
      const ct = el.currentTime || 0;
      if (ct > maxHeardRef.current + 0.5) el.currentTime = maxHeardRef.current;
      if (ct < startSec) el.currentTime = startSec;
      maxHeardRef.current = Math.max(maxHeardRef.current, ct);
      if (endSec && ct >= endSec) {
        finalizeAudio("segment_end");
      }
    };

    const onEnded = () => finalizeAudio("natural_end");

    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("canplay", onCanPlay);
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("ended", onEnded);

    return () => {
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("ended", onEnded);
    };
  }, [phase, startSec, endSec, audioBoot]); // ← audioBoot تضمین می‌کند برای سؤال جدید setup می‌شه

  const acknowledgeAutoplay = () => {
    const el = audioRef.current;
    if (!el) return;
    el.play()
      .then(() => {
        log("audio:autoplay-allowed");
        setAutoplayNeeded(false);
      })
      .catch(() => {});
  };

  // ====== Phase 2: prep (mic here) ======
  useEffect(() => {
    if (phase !== "prep") return;
    log("prep:start", { prepTimeSec });

    let cancelled = false;

    (async () => {
      try {
        if (!mediaStreamRef.current) {
          mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          log("prep:mic:ok");
        }
      } catch (e) {
        log("prep:mic:fail", e);
        setError("Microphone permission denied or unavailable.");
        setPhase("done");
        return;
      }
      if (cancelled) return;

      if (prepTimeSec <= 0) {
        log("prep:skip→record");
        setPhase("record");
        return;
      }

      setPrepLeft(prepTimeSec);
      const t0 = performance.now();
      const step = () => {
        if (cancelled) return;
        const left = Math.max(
          0,
          Math.ceil(prepTimeSec - (performance.now() - t0) / 1000)
        );
        setPrepLeft(left);
        if (left <= 0) {
          log("prep:end→record");
          setPhase("record");
          return;
        }
        rafPrepRef.current = requestAnimationFrame(step);
      };
      rafPrepRef.current = requestAnimationFrame(step);
    })();

    return () => {
      try {
        cancelAnimationFrame(rafPrepRef.current);
      } catch {}
      cancelled = true;
    };
  }, [phase, prepTimeSec]);

  // refs برای خارج کردن توابع از dependency و جلوگیری از re-run
  const onAnsRef = useRef(onAnswerChange);
  const onContRef = useRef(onContinue);
  const respTimeRef = useRef(respTimeSec);
  const maxSecRef = useRef(maxSec);
  const listenedMsRef = useRef(listenedMs);
  useEffect(() => {
    onAnsRef.current = onAnswerChange;
  }, [onAnswerChange]);
  useEffect(() => {
    onContRef.current = onContinue;
  }, [onContinue]);
  useEffect(() => {
    respTimeRef.current = respTimeSec;
  }, [respTimeSec]);
  useEffect(() => {
    maxSecRef.current = maxSec;
  }, [maxSec]);
  useEffect(() => {
    listenedMsRef.current = listenedMs;
  }, [listenedMs]);

  // ====== Phase 3: record (stable deps) ======
  useEffect(() => {
    if (phase !== "record") return;
    if (recordingActiveRef.current) {
      log("record:skip-duplicate-start");
      return;
    }
    recordingActiveRef.current = true;
    log("record:start", { respTimeSec, maxSec });

    let stopped = false;
    let alive = true;

    const stopSafely = (mr) => {
      if (stopped) return;
      stopped = true;
      try {
        mr?.stop();
      } catch {}
    };

    (async () => {
      try {
        if (!mediaStreamRef.current) {
          mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          log("record:mic:late-ok");
        }

        const mime = pickMime();
        const mr = new MediaRecorder(
          mediaStreamRef.current,
          mime ? { mimeType: mime } : undefined
        );
        mediaRecorderRef.current = mr;
        chunksRef.current = [];

        mr.ondataavailable = (e) =>
          e.data?.size && chunksRef.current.push(e.data);

        mr.onstop = async () => {
          log("record:onstop");
          try {
            const blob = new Blob(chunksRef.current, {
              type: mr.mimeType || "audio/webm",
            });
            let meta = {
              url: URL.createObjectURL(blob),
              mimeType: blob.type,
              size: blob.size,
              durationMs: Math.max(
                0,
                Math.round(performance.now() - recStartRef.current)
              ),
            };

            if (uploadOnStop) {
              try {
                const file = new File(
                  [blob],
                  `speaking-${item?._id || "q"}-${Date.now()}.webm`,
                  {
                    type: blob.type || "audio/webm",
                  }
                );
                const fd = new FormData();
                fd.append("file", file);
                const user = JSON.parse(localStorage.getItem("user") || "{}");
                const headers = user?.token
                  ? { "x-access-token": user.token }
                  : undefined;

                const res = await fetch(`${BASE_URL || ""}/media`, {
                  method: "POST",
                  headers,
                  body: fd,
                });
                if (res.ok) {
                  const json = await res.json().catch(() => null);
                  const mediaDoc = json?.data;
                  if (mediaDoc?.url) {
                    if (meta.url?.startsWith("blob:"))
                      URL.revokeObjectURL(meta.url);
                    meta = {
                      ...meta,
                      url: mediaDoc.url,
                      mediaId: mediaDoc._id,
                      serverMimeType: mediaDoc.mimeType,
                    };
                  }
                }
              } catch (e) {
                log("record:upload:fail", e);
              }
            }

            onAnsRef.current?.({
              phase: "done",
              listenedMs: listenedMsRef.current,
              recording: meta,
            });
            setPhase("done");
            if (!didAutoNextRef.current) {
              didAutoNextRef.current = true;
              setTimeout(() => onContRef.current?.(), 0);
            }
          } catch (e) {
            setError("Failed to finalize recording.");
          } finally {
            try {
              mediaStreamRef.current?.getTracks()?.forEach((t) => t.stop());
            } catch {}
            mediaStreamRef.current = null;
            recordingActiveRef.current = false;
          }
        };

        mr.start(100);
        recStartRef.current = performance.now();

        const hardLimit = Math.min(respTimeRef.current, maxSecRef.current);
        const loop = () => {
          if (!alive) return;
          const elapsed = (performance.now() - recStartRef.current) / 1000;
          setRespLeft(Math.max(0, Math.ceil(respTimeRef.current - elapsed)));
          if (elapsed >= hardLimit) {
            log("record:hard-limit-reached", { elapsed, hardLimit });
            stopSafely(mr);
            return;
          }
          rafRecRef.current = requestAnimationFrame(loop);
        };
        rafRecRef.current = requestAnimationFrame(loop);
      } catch (e) {
        log("record:start:fail", e);
        setError("Microphone access denied.");
        setPhase("done");
        recordingActiveRef.current = false;
      }
    })();

    return () => {
      // فقط تمیزکاری UI؛ stop را به منطق stopSafely/limit بسپار
      try {
        cancelAnimationFrame(rafRecRef.current);
      } catch {}
      alive = false;
    };
  }, [phase]); // ❗️فقط فاز

  // ====== Render ======
  const promptHtml = prompt?.textHtml || "";

  return (
    <div className="rounded-lg bg-white p-4">
      {!hideStem && (item?.stem?.en || item?.stem?.fa) && (
        <div
          dir="ltr"
          className="prose max-w-none mb-4"
          dangerouslySetInnerHTML={{ __html: item.stem.en || item.stem.fa }}
        />
      )}

      {promptHtml && (
        <div
          dir="ltr"
          className="prose max-w-none mb-4 text-gray-800"
          dangerouslySetInnerHTML={{ __html: promptHtml }}
        />
      )}

      {/* Phase: AUDIO (hidden) */}
      {phase === "audio" && (
        <div className="relative">
          <audio
            ref={audioRef}
            src={url}
            autoPlay
            playsInline
            preload="auto"
            controls={false}
            style={{ display: "none" }}
          />
          {autoplayNeeded && (
            <button
              onClick={acknowledgeAutoplay}
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-md border p-6"
            >
              <span className="px-4 py-2 rounded-lg bg-emerald-600 text-white">
                Tap to allow audio
              </span>
            </button>
          )}
        </div>
      )}

      {/* Phase: PREP */}
      {phase === "prep" && (
        <div className="rounded-md border bg-amber-50 p-6 text-center">
          <div className="text-sm text-gray-700 mb-2">Preparation time</div>
          <div className="text-4xl font-mono">{prepLeft}</div>
          <div className="text-xs text-gray-500 mt-2">Get ready to speak…</div>
        </div>
      )}

      {/* Phase: RECORD */}
      {phase === "record" && (
        <div className="rounded-md border bg-emerald-50 p-6 text-center">
          <div className="text-sm text-gray-700 mb-2">Recording</div>
          <div className="text-4xl font-mono">{fmt(respLeft)}</div>
          <div className="text-xs text-gray-500 mt-2">
            Recording will stop automatically.
          </div>
        </div>
      )}

      {/* Phase: DONE */}
      {phase === "done" && (
        <div className="rounded-md border bg-white p-6 text-center">
          <div className="text-emerald-700 font-medium">
            Response captured. You can continue.
          </div>
        </div>
      )}

      {error && <div className="mt-3 text-sm text-rose-600">{error}</div>}
    </div>
  );
}
